import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { apiFetch } from "../../api";

function useVehicleOptions() {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef(null);

  const fetchOptions = React.useCallback(async (q) => {
    if (controllerRef.current) controllerRef.current.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;
    try {
      setLoading(true);
      const res = await apiFetch(`/api/vehiculos/?q=${encodeURIComponent(q || "")}`, {
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error("no response");
      const data = await res.json();
      setOptions(
        (Array.isArray(data) ? data : [])
          .map((x) => {
            if (typeof x === "string") return { id: null, label: x };
            return { id: x.id ?? null, label: x.modelo || x.nombre || x.name };
          })
          .filter((o) => o && o.label)
      );
    } catch (e) {
      if (e.name !== "AbortError") setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, fetchOptions, loading };
}

function useStaticOptions(list) {
  const [options, setOptions] = React.useState(
    list.map((x, i) => ({ id: i, label: x }))
  );

  const fetchOptions = React.useCallback((q) => {
    const query = String(q || "").trim().toLowerCase();
    const mapped = list.map((x, i) => ({ id: i, label: x }));
    setOptions(query ? mapped.filter((o) => o.label.toLowerCase().includes(query)) : mapped);
  }, [list]);

  return { options, fetchOptions };
}

const OBTENIDO_LIST = [
  "Meta Ads",
  "Messenger",
  "Instagram",
  "Facebook",
  "Contacto Directo",
  "Otro",
];

export default function CrearClienteForm({
  open,
  onClose,
  form,
  onChange,
  onSubmit,
  error,
  loading = false,
}) {
  const [localError, setLocalError] = React.useState(null);
  const [consultaInput, setConsultaInput] = React.useState("");
  const vehicleApi = useVehicleOptions();
  const obtenidoApi = useStaticOptions(OBTENIDO_LIST);

  const disabled = Boolean(loading);
  const consultas = Array.isArray(form.consultas) ? form.consultas : [];
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailInvalid = Boolean(form.email) && !emailRegex.test(form.email);

  React.useEffect(() => {
    if (!open) {
      setConsultaInput("");
      setLocalError(null);
    }
  }, [open]);

  const emit = (name, value) => onChange({ target: { name, value } });

  const addConsulta = () => {
    const value = consultaInput.trim();
    if (!value) {
      setLocalError("Elegí o escribí un vehículo consultado.");
      return;
    }
    emit("consultas", [...consultas, { vehiculo_consultado: value }]);
    emit("consulta", consultas.length === 0 ? value : form.consulta || consultas[0]?.vehiculo_consultado || value);
    setConsultaInput("");
    setLocalError(null);
  };

  const removeConsulta = (idx) => {
    const next = consultas.filter((_, i) => i !== idx);
    emit("consultas", next);
    emit("consulta", next[0]?.vehiculo_consultado || "");
  };

  const handleSubmitLocal = (e) => {
    e?.preventDefault?.();
    if (!form.nombre || String(form.nombre).trim() === "") {
      setLocalError('El campo "Nombre" es obligatorio.');
      return;
    }
    if (consultas.length === 0) {
      setLocalError("Agregá al menos una consulta.");
      return;
    }
    setLocalError(null);
    onSubmit?.(e);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#4c5d86", fontWeight: 700 }}>Cliente</Typography>
            <Typography sx={{ fontSize: 24, color: "#111827", fontWeight: 900 }}>Nuevo cliente</Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small" disabled={disabled}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mb: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {localError && <Alert severity="error">{localError}</Alert>}
        </Stack>

        <Box component="form" onSubmit={handleSubmitLocal} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.35fr" }, gap: 2 }}>
          <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "#06185f", mb: 1.5 }}>Información básica</Typography>
            <Stack spacing={1.5}>
              <TextField label="Nombre" name="nombre" value={form.nombre || ""} onChange={onChange} disabled={disabled} required fullWidth />
              <TextField label="Teléfono" name="telefono" value={form.telefono || ""} onChange={onChange} disabled={disabled} fullWidth inputProps={{ inputMode: "numeric" }} />
              <TextField label="Email" name="email" type="email" value={form.email || ""} onChange={onChange} disabled={disabled} error={emailInvalid} helperText={emailInvalid ? "Ingresá un email válido." : ""} fullWidth />
              <Autocomplete
                freeSolo
                options={obtenidoApi.options}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                value={obtenidoApi.options.find((o) => o.label === form.obtenido_de) || form.obtenido_de || null}
                onOpen={() => obtenidoApi.fetchOptions("")}
                onInputChange={(e, value) => emit("obtenido_de", value)}
                onChange={(e, value) => emit("obtenido_de", typeof value === "string" ? value : value?.label || "")}
                renderInput={(params) => <TextField {...params} label="Origen" disabled={disabled} />}
              />
              <TextField
                label="Observación"
                name="observacion"
                value={form.observacion || ""}
                onChange={onChange}
                disabled={disabled}
                fullWidth
                multiline
                minRows={3}
              />
            </Stack>
          </Box>

          <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "#06185f", mb: 1.5 }}>Consultas</Typography>
            <Stack spacing={1.25}>
              {consultas.length === 0 ? (
                <Box sx={{ border: "1px dashed #c9d7ea", borderRadius: "8px", p: 2, textAlign: "center", color: "#4c5d86" }}>
                  Todavía no hay consultas cargadas.
                </Box>
              ) : (
                consultas.map((consulta, idx) => (
                  <Box key={`${consulta.vehiculo_consultado}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #e4ebf5", borderRadius: "8px", p: 1.25 }}>
                    <DirectionsCarIcon sx={{ color: "#0059f6", fontSize: 20 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, color: "#111827", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {consulta.vehiculo_consultado}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#4c5d86" }}>Se guardará al crear el cliente</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeConsulta(idx)} disabled={disabled}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}

              <Divider />

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
                <Autocomplete
                  freeSolo
                  options={vehicleApi.options}
                  getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                  inputValue={consultaInput}
                  onInputChange={(e, value, reason) => {
                    setConsultaInput(value);
                    if (reason === "input") vehicleApi.fetchOptions(value);
                  }}
                  onOpen={() => vehicleApi.fetchOptions("")}
                  onChange={(e, value) => setConsultaInput(typeof value === "string" ? value : value?.label || "")}
                  loading={vehicleApi.loading}
                  noOptionsText="No hay vehículos"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vehículo consultado"
                      disabled={disabled}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {vehicleApi.loading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button variant="outlined" onClick={addConsulta} disabled={disabled} startIcon={<AddCircleOutlineIcon />}>
                  Nueva consulta
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={disabled}>Cancelar</Button>
        <Button onClick={handleSubmitLocal} variant="contained" disabled={disabled || emailInvalid}>
          {loading ? "Creando..." : "Crear cliente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
